import { useParams, Link } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import ProductSpotlight from "@/components/ProductSpotlight";
import { PRODUCTS, CATEGORIES, STICKER_SUBCATEGORIES, productsByCategory } from "@/data/mellow";

const ComingSoonBox = ({ text }) => (
  <p className="rounded-[2rem] border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] p-8 text-center font-cursive text-3xl text-[#78353A]/70">{text}</p>
);

const StickerSubcategories = ({ items }) => (
  <div className="space-y-12" data-testid="sticker-subcategories">
    {STICKER_SUBCATEGORIES.map((sub) => {
      const list = items.filter((p) => p.subcategory === sub.slug);
      return (
        <div key={sub.slug} data-testid={`sticker-sub-${sub.slug}`}>
          <h3 className="font-cursive text-3xl md:text-4xl text-[#78353A] mb-4 flex items-center gap-3">
            <span className="daisy-deco !w-6 !h-6" aria-hidden="true"></span>{sub.name}
          </h3>
          {list.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {list.map((p) => <ProductCard key={p.lookup_key} product={p} />)}
            </div>
          ) : (
            <ComingSoonBox text="Cette série pousse encore… bientôt ✿" />
          )}
        </div>
      );
    })}
  </div>
);

const CategoryContent = ({ cat, items }) => {
  if (cat.status) return <ComingSoonBox text={`${cat.status} ✿`} />;
  if (cat.slug === "stickers") return <StickerSubcategories items={items} />;
  if (items.length === 0) return <ComingSoonBox text="Cette rubrique pousse encore… bientôt ✿" />;
  if (items.length === 1 && items[0].themes) return <ProductSpotlight product={items[0]} eyebrow="Notre carnet" />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
      {items.map((p) => <ProductCard key={p.lookup_key} product={p} />)}
    </div>
  );
};

export default function Boutique() {
  const { category } = useParams();
  const list = category ? productsByCategory(category) : PRODUCTS;
  const active = category ? CATEGORIES.find((c) => c.slug === category) : null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10">
        <p className="font-cursive text-2xl text-[#78353A]/70">La boutique</p>
        <h1 className="font-cursive text-6xl md:text-7xl text-[#78353A] leading-none">{active ? active.name : "La boutique MiniMellow"}</h1>
        {active && <p className="mt-3 text-[#4A3B32]/80">{active.tagline}</p>}
      </div>

      {CATEGORIES.length > 1 && (
      <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filters">
        <Link to="/boutique" className={`px-4 py-2 rounded-full font-cursive text-xl border-2 border-dashed ${!category ? "bg-[#78353A] text-[#FAF9F6] border-[#78353A]" : "border-[#D1D5CB] text-[#4A3B32] hover:bg-[#EAECE1]"}`}>Tout</Link>
        {CATEGORIES.map((c) => (
          <Link
            key={c.slug}
            to={`/boutique/${c.slug}`}
            className={`px-4 py-2 rounded-full font-cursive text-xl border-2 border-dashed ${category === c.slug ? "bg-[#78353A] text-[#FAF9F6] border-[#78353A]" : "border-[#D1D5CB] text-[#4A3B32] hover:bg-[#EAECE1]"}`}
            data-testid={`filter-${c.slug}`}
          >
            {c.name}
          </Link>
        ))}
      </div>
      )}

      {active ? (
        <CategoryContent cat={active} items={list} />
      ) : (
        <div className="space-y-20">
          {CATEGORIES.map((cat) => (
            <section key={cat.slug} id={cat.slug} data-testid={`shop-section-${cat.slug}`}>
              <p className="font-cursive text-2xl text-[#78353A]/70">{cat.tagline}</p>
              <h2 className="font-cursive text-5xl md:text-6xl text-[#78353A] leading-none mb-8 flex flex-wrap items-center gap-4">
                {cat.name}
                {cat.status && <span className="font-cursive text-xl px-4 py-1 rounded-full bg-[#FFF2B2] text-[#78353A]">{cat.status}</span>}
              </h2>
              <CategoryContent cat={cat} items={PRODUCTS.filter((p) => p.category === cat.slug)} />
            </section>
          ))}
        </div>
      )}
      <p className="mt-10 text-center font-cursive text-2xl text-[#78353A]/70" data-testid="shop-coming-soon">D'autres douceurs arrivent bientôt ✿</p>
    </div>
  );
}
